'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import { MapPin, Phone, Package, DollarSign, CheckCircle2, Truck, Navigation, Clock, ChevronDown, ChevronUp, AlertCircle, Circle, MessageSquare, Sun, Moon, Bell, LogOut, ClipboardList } from 'lucide-react';
import { useTheme } from '@/components/ui/ThemeProvider';
import { ThemeProvider } from '@/components/ui/ThemeProvider';

// ─── Types ────────────────────────────────────────────────────────────────────

type DeliveryStatus = 'pending' | 'en-route' | 'arrived' | 'delivered' | 'failed';

interface DeliveryItem {
  name: string;
  size: string;
  qty: number;
}

interface Delivery {
  id: string;
  orderId: string;
  customer: string;
  phone: string;
  address: string;
  area: string;
  notes: string;
  items: DeliveryItem[];
  amountDue: number;
  status: DeliveryStatus;
  scheduledWindow: string;
  sequence: number;
}

interface CashEntry {
  amountCollected: string;
  notes: string;
}

// ─── Mock Data ────────────────────────────────────────────────────────────────

const initialDeliveries: Delivery[] = [
  {
    id: 'del-001',
    orderId: 'ORD-1042',
    customer: 'Sandra Charles',
    phone: '+1 268 770 5590',
    address: '14 Redcliffe Street, English Harbour',
    area: 'English Harbour',
    notes: 'Leave at gate if no answer. Ring twice.',
    items: [
      { name: 'Original Hummus', size: 'Large', qty: 2 },
      { name: 'Roasted Garlic Hummus', size: 'Medium', qty: 1 },
    ],
    amountDue: 48,
    status: 'delivered',
    scheduledWindow: '09:00 – 11:00',
    sequence: 1,
  },
  {
    id: 'del-002',
    orderId: 'ORD-1051',
    customer: 'Yvette Thomas',
    phone: '+1 268 765 0934',
    address: '7 Friars Hill Road, St. John\'s',
    area: 'Friars Hill',
    notes: 'Call 10 minutes before arrival.',
    items: [
      { name: 'Original Hummus', size: 'Small', qty: 3 },
      { name: 'Spicy Hummus', size: 'Small', qty: 2 },
    ],
    amountDue: 35,
    status: 'en-route',
    scheduledWindow: '10:00 – 12:00',
    sequence: 2,
  },
  {
    id: 'del-003',
    orderId: 'ORD-1058',
    customer: 'Calvin Peters',
    phone: '+1 268 723 4412',
    address: '22 Long Street, St. John\'s South',
    area: "St. John's South",
    notes: 'Family pack — handle with care.',
    items: [
      { name: 'Original Hummus', size: 'Family Pack', qty: 1 },
      { name: 'Roasted Garlic Hummus', size: 'Large', qty: 1 },
    ],
    amountDue: 62,
    status: 'pending',
    scheduledWindow: '11:00 – 13:00',
    sequence: 3,
  },
  {
    id: 'del-004',
    orderId: 'ORD-1063',
    customer: 'Monique Henry',
    phone: '+1 268 799 1108',
    address: '5 Popeshead Street, St. John\'s',
    area: "St. John's Central",
    notes: '',
    items: [
      { name: 'Original Hummus', size: 'Medium', qty: 2 },
    ],
    amountDue: 28,
    status: 'pending',
    scheduledWindow: '12:00 – 14:00',
    sequence: 4,
  },
  {
    id: 'del-005',
    orderId: 'ORD-1071',
    customer: 'Trevor Simon',
    phone: '+1 268 712 6630',
    address: '3 Newgate Street, St. John\'s',
    area: "St. John's North",
    notes: 'Intercom code: 4421',
    items: [
      { name: 'Spicy Hummus', size: 'Large', qty: 1 },
      { name: 'Original Hummus', size: 'Small', qty: 2 },
    ],
    amountDue: 41,
    status: 'pending',
    scheduledWindow: '13:00 – 15:00',
    sequence: 5,
  },
];

// ─── Status Config ────────────────────────────────────────────────────────────

const statusConfig: Record<DeliveryStatus, { label: string; color: string; bg: string; dotColor: string }> = {
  pending: { label: 'Pending', color: 'text-muted-foreground', bg: 'bg-muted', dotColor: 'text-muted-foreground' },
  'en-route': { label: 'En Route', color: 'text-info', bg: 'bg-info/10', dotColor: 'text-info' },
  arrived: { label: 'Arrived', color: 'text-warning', bg: 'bg-warning/10', dotColor: 'text-warning' },
  delivered: { label: 'Delivered', color: 'text-positive', bg: 'bg-positive/10', dotColor: 'text-positive' },
  failed: { label: 'Failed', color: 'text-negative', bg: 'bg-negative/10', dotColor: 'text-negative' },
};

const statusFlow: DeliveryStatus[] = ['pending', 'en-route', 'arrived', 'delivered'];

// ─── Cash Collection Modal ────────────────────────────────────────────────────

function CashCollectionModal({
  delivery,
  onClose,
  onSubmit,
}: {
  delivery: Delivery;
  onClose: () => void;
  onSubmit: (id: string, entry: CashEntry) => void;
}) {
  const [entry, setEntry] = useState<CashEntry>({ amountCollected: String(delivery.amountDue), notes: '' });
  const collected = parseFloat(entry.amountCollected) || 0;
  const diff = collected - delivery.amountDue;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-card rounded-2xl border border-border shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <DollarSign size={16} />
            </div>
            <div>
              <h3 className="text-sm font-700 text-foreground">Cash Collection</h3>
              <p className="text-xs text-muted-foreground">{delivery.customer} · {delivery.orderId}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <ChevronDown size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Amount Due */}
          <div className="p-3 rounded-xl bg-muted/50 flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Amount Due</span>
            <span className="text-lg font-700 text-foreground font-tabular">${delivery.amountDue.toFixed(2)}</span>
          </div>

          {/* Amount Collected Input */}
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5">Amount Collected (EC$)</label>
            <input
              type="number"
              value={entry.amountCollected}
              onChange={e => setEntry(prev => ({ ...prev, amountCollected: e.target.value }))}
              className="input-field text-lg font-700 font-tabular"
              placeholder="0.00"
              min="0"
              step="0.01"
            />
          </div>

          {/* Difference */}
          {entry.amountCollected !== '' && (
            <div className={`p-3 rounded-xl flex items-center justify-between ${diff === 0 ? 'bg-positive/10' : diff > 0 ? 'bg-info/10' : 'bg-negative/10'}`}>
              <span className={`text-sm font-600 ${diff === 0 ? 'text-positive' : diff > 0 ? 'text-info' : 'text-negative'}`}>
                {diff === 0 ? '✓ Exact amount' : diff > 0 ? `Overpaid by $${diff.toFixed(2)}` : `Short by $${Math.abs(diff).toFixed(2)}`}
              </span>
              <span className={`text-sm font-700 font-tabular ${diff === 0 ? 'text-positive' : diff > 0 ? 'text-info' : 'text-negative'}`}>
                {diff >= 0 ? '+' : ''}${diff.toFixed(2)}
              </span>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-600 text-muted-foreground mb-1.5">Notes (optional)</label>
            <textarea
              value={entry.notes}
              onChange={e => setEntry(prev => ({ ...prev, notes: e.target.value }))}
              className="input-field resize-none"
              rows={2}
              placeholder="e.g. Customer paid in coins, gave change…"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          <button
            onClick={() => onSubmit(delivery.id, entry)}
            className="btn-primary flex-1"
          >
            <CheckCircle2 size={15} />
            Confirm Collection
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Delivery Card ────────────────────────────────────────────────────────────

function DeliveryCard({
  delivery,
  onStatusChange,
  onOpenCash,
}: {
  delivery: Delivery;
  onStatusChange: (id: string, status: DeliveryStatus) => void;
  onOpenCash: (delivery: Delivery) => void;
}) {
  const [expanded, setExpanded] = useState(delivery.status === 'en-route' || delivery.status === 'arrived');
  const cfg = statusConfig[delivery.status];
  const isDelivered = delivery.status === 'delivered';
  const isFailed = delivery.status === 'failed';
  const isDone = isDelivered || isFailed;

  const nextStatus = statusFlow[statusFlow.indexOf(delivery.status) + 1];
  const nextLabel: Record<DeliveryStatus, string> = {
    pending: 'Start Route',
    'en-route': 'Mark Arrived',
    arrived: 'Mark Delivered',
    delivered: '',
    failed: '',
  };

  return (
    <div className={`card-elevated overflow-hidden transition-all duration-200 ${isDone ? 'opacity-70' : ''}`}>
      {/* Card Header */}
      <button
        className="w-full text-left p-4 flex items-start gap-3 hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(e => !e)}
      >
        {/* Sequence badge */}
        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-700 flex-shrink-0 mt-0.5 ${isDelivered ? 'bg-positive/10 text-positive' : isFailed ? 'bg-negative/10 text-negative' : 'gradient-primary text-white'}`}>
          {isDelivered ? <CheckCircle2 size={14} /> : isFailed ? <AlertCircle size={14} /> : delivery.sequence}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-0.5">
            <p className="text-sm font-700 text-foreground truncate">{delivery.customer}</p>
            <span className={`text-[10px] font-700 px-2 py-0.5 rounded-full flex-shrink-0 ${cfg.bg} ${cfg.color}`}>
              {cfg.label}
            </span>
          </div>
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <MapPin size={10} className="flex-shrink-0" />
            <span className="truncate">{delivery.area}</span>
            <span className="mx-1">·</span>
            <Clock size={10} className="flex-shrink-0" />
            <span>{delivery.scheduledWindow}</span>
          </div>
          <div className="flex items-center justify-between mt-1.5">
            <span className="text-xs text-muted-foreground">{delivery.orderId} · {delivery.items.reduce((s, i) => s + i.qty, 0)} items</span>
            <span className="text-sm font-700 text-foreground font-tabular">${delivery.amountDue.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex-shrink-0 text-muted-foreground mt-1">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Expanded Details */}
      {expanded && (
        <div className="border-t border-border">
          {/* Customer Details */}
          <div className="px-4 py-3 space-y-2.5">
            {/* Address */}
            <div className="flex items-start gap-2.5">
              <div className="p-1.5 rounded-lg bg-muted text-muted-foreground flex-shrink-0 mt-0.5">
                <MapPin size={13} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600 text-muted-foreground mb-0.5">Delivery Address</p>
                <p className="text-sm text-foreground">{delivery.address}</p>
              </div>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(delivery.address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                title="Open in Maps"
              >
                <Navigation size={13} />
              </a>
            </div>

            {/* Phone */}
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-lg bg-muted text-muted-foreground flex-shrink-0">
                <Phone size={13} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600 text-muted-foreground mb-0.5">Customer Phone</p>
                <p className="text-sm text-foreground font-tabular">{delivery.phone}</p>
              </div>
              <a
                href={`tel:${delivery.phone}`}
                className="flex-shrink-0 p-1.5 rounded-lg bg-positive/10 text-positive hover:bg-positive/20 transition-colors"
                title="Call customer"
              >
                <Phone size={13} />
              </a>
            </div>

            {/* Items */}
            <div className="flex items-start gap-2.5">
              <div className="p-1.5 rounded-lg bg-muted text-muted-foreground flex-shrink-0 mt-0.5">
                <Package size={13} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-600 text-muted-foreground mb-1">Order Items</p>
                <div className="space-y-1">
                  {delivery.items.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between text-xs">
                      <span className="text-foreground">{item.name} <span className="text-muted-foreground">({item.size})</span></span>
                      <span className="font-700 text-foreground font-tabular">×{item.qty}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Notes */}
            {delivery.notes && (
              <div className="flex items-start gap-2.5">
                <div className="p-1.5 rounded-lg bg-warning/10 text-warning flex-shrink-0 mt-0.5">
                  <MessageSquare size={13} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-600 text-muted-foreground mb-0.5">Delivery Notes</p>
                  <p className="text-sm text-foreground italic">{delivery.notes}</p>
                </div>
              </div>
            )}

            {/* Amount Due */}
            <div className="p-3 rounded-xl bg-muted/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign size={14} className="text-muted-foreground" />
                <span className="text-sm font-600 text-muted-foreground">Amount Due</span>
              </div>
              <span className="text-lg font-700 text-foreground font-tabular">${delivery.amountDue.toFixed(2)}</span>
            </div>
          </div>

          {/* Action Buttons */}
          {!isDone && (
            <div className="px-4 pb-4 flex gap-2.5">
              {/* Cash Collection */}
              <button
                onClick={() => onOpenCash(delivery)}
                className="btn-secondary flex-1 text-xs"
              >
                <DollarSign size={13} />
                Collect Cash
              </button>

              {/* Next Status */}
              {nextStatus && (
                <button
                  onClick={() => onStatusChange(delivery.id, nextStatus)}
                  className="btn-primary flex-1 text-xs"
                >
                  {nextStatus === 'en-route' && <Truck size={13} />}
                  {nextStatus === 'arrived' && <MapPin size={13} />}
                  {nextStatus === 'delivered' && <CheckCircle2 size={13} />}
                  {nextLabel[delivery.status]}
                </button>
              )}

              {/* Mark Failed */}
              <button
                onClick={() => onStatusChange(delivery.id, 'failed')}
                className="p-2.5 rounded-lg bg-negative/10 text-negative hover:bg-negative/20 transition-colors flex-shrink-0"
                title="Mark as failed delivery"
              >
                <AlertCircle size={14} />
              </button>
            </div>
          )}

          {/* Delivered state */}
          {isDelivered && (
            <div className="px-4 pb-4">
              <div className="flex items-center gap-2 p-3 rounded-xl bg-positive/10">
                <CheckCircle2 size={15} className="text-positive flex-shrink-0" />
                <span className="text-sm font-600 text-positive">Delivered successfully</span>
              </div>
            </div>
          )}

          {/* Failed state */}
          {isFailed && (
            <div className="px-4 pb-4">
              <div className="flex items-center gap-2 p-3 rounded-xl bg-negative/10">
                <AlertCircle size={15} className="text-negative flex-shrink-0" />
                <span className="text-sm font-600 text-negative">Delivery failed — contact manager</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Summary Strip ────────────────────────────────────────────────────────────

function SummaryStrip({ deliveries }: { deliveries: Delivery[] }) {
  const total = deliveries.length;
  const delivered = deliveries.filter(d => d.status === 'delivered').length;
  const onRoute = deliveries.filter(d => d.status === 'en-route').length;
  const pending = deliveries.filter(d => d.status === 'pending').length;
  const totalCash = deliveries.filter(d => d.status === 'delivered').reduce((s, d) => s + d.amountDue, 0);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
      {[
        { label: 'Total Deliveries', value: `${delivered}/${total}`, sub: 'completed', color: 'text-foreground', icon: <Package size={14} /> },
        { label: 'En Route', value: String(onRoute), sub: 'in progress', color: 'text-info', icon: <Truck size={14} /> },
        { label: 'Pending', value: String(pending), sub: 'not started', color: 'text-warning', icon: <Clock size={14} /> },
        { label: 'Cash Collected', value: `$${totalCash.toFixed(2)}`, sub: 'from deliveries', color: 'text-positive', icon: <DollarSign size={14} /> },
      ].map(item => (
        <div key={item.label} className="card-elevated p-3.5">
          <div className="flex items-center gap-1.5 text-muted-foreground mb-1.5">
            {item.icon}
            <span className="text-[11px] font-600 uppercase tracking-wide">{item.label}</span>
          </div>
          <p className={`text-xl font-700 font-tabular ${item.color}`}>{item.value}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{item.sub}</p>
        </div>
      ))}
    </div>
  );
}

// ─── Driver Topbar ────────────────────────────────────────────────────────────

function DriverTopbar() {
  const { theme, toggleTheme } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => { setMounted(true); }, []);

  return (
    <header className="h-14 border-b border-border bg-card flex items-center px-4 sm:px-6 gap-3 flex-shrink-0">
      {/* Logo */}
      <div className="flex items-center gap-2.5 flex-shrink-0">
        <Image
          src="/assets/images/WhatsApp_Image_2026-07-30_at_10.00.29-1785430981886.jpeg"
          alt="HummusHub official logo"
          width={34}
          height={34}
          className="rounded-lg object-cover flex-shrink-0"
          priority
        />
        <div className="hidden sm:block">
          <span className="font-700 text-sm text-foreground leading-tight block">HummusHub</span>
          <span className="text-[10px] text-muted-foreground">Driver Portal</span>
        </div>
      </div>

      <div className="flex-1" />

      {/* Driver info */}
      <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted/60">
        <div className="w-6 h-6 rounded-full gradient-primary flex items-center justify-center text-[10px] font-700 text-white flex-shrink-0">
          DM
        </div>
        <div>
          <p className="text-xs font-600 text-foreground leading-tight">Derrick Manley</p>
          <p className="text-[10px] text-muted-foreground">Driver</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <button
          onClick={toggleTheme}
          className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Toggle theme"
        >
          {mounted ? (theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />) : <Moon size={17} />}
        </button>
        <button className="relative p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" aria-label="Notifications">
          <Bell size={17} />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-negative rounded-full" />
        </button>
        <button className="p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-negative transition-colors" aria-label="Sign out" title="Sign out">
          <LogOut size={17} />
        </button>
      </div>
    </header>
  );
}

// ─── End of Day Reconciliation ────────────────────────────────────────────────

function ReconciliationPanel({ deliveries }: { deliveries: Delivery[] }) {
  const [submitted, setSubmitted] = useState(false);
  const [actualCash, setActualCash] = useState('');
  const [notes, setNotes] = useState('');

  const expectedCash = deliveries.filter(d => d.status === 'delivered').reduce((s, d) => s + d.amountDue, 0);
  const actual = parseFloat(actualCash) || 0;
  const diff = actual - expectedCash;

  if (submitted) {
    return (
      <div className="card-elevated p-5 flex items-center gap-3">
        <CheckCircle2 size={20} className="text-positive flex-shrink-0" />
        <div>
          <p className="text-sm font-700 text-foreground">Reconciliation Submitted</p>
          <p className="text-xs text-muted-foreground">Awaiting manager approval</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card-elevated p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="p-2 rounded-lg bg-muted text-muted-foreground">
          <ClipboardList size={16} />
        </div>
        <div>
          <h2 className="text-base font-700 text-foreground">End of Day Reconciliation</h2>
          <p className="text-xs text-muted-foreground">Submit your cash total for manager review</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 rounded-xl bg-muted/50">
          <p className="text-xs text-muted-foreground mb-0.5">Expected Cash</p>
          <p className="text-lg font-700 text-foreground font-tabular">${expectedCash.toFixed(2)}</p>
        </div>
        <div className="p-3 rounded-xl bg-muted/50">
          <p className="text-xs text-muted-foreground mb-0.5">Deliveries Done</p>
          <p className="text-lg font-700 text-foreground font-tabular">
            {deliveries.filter(d => d.status === 'delivered').length}/{deliveries.length}
          </p>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1.5">Actual Cash on Hand (EC$)</label>
          <input
            type="number"
            value={actualCash}
            onChange={e => setActualCash(e.target.value)}
            className="input-field text-base font-700 font-tabular"
            placeholder="0.00"
            min="0"
            step="0.01"
          />
        </div>

        {actualCash !== '' && (
          <div className={`p-3 rounded-xl flex items-center justify-between ${diff === 0 ? 'bg-positive/10' : diff > 0 ? 'bg-info/10' : 'bg-negative/10'}`}>
            <span className={`text-sm font-600 ${diff === 0 ? 'text-positive' : diff > 0 ? 'text-info' : 'text-negative'}`}>
              {diff === 0 ? '✓ Balanced' : diff > 0 ? 'Surplus' : 'Shortage'}
            </span>
            <span className={`text-sm font-700 font-tabular ${diff === 0 ? 'text-positive' : diff > 0 ? 'text-info' : 'text-negative'}`}>
              {diff >= 0 ? '+' : ''}${diff.toFixed(2)}
            </span>
          </div>
        )}

        <div>
          <label className="block text-xs font-600 text-muted-foreground mb-1.5">Notes</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="input-field resize-none"
            rows={2}
            placeholder="Any discrepancies or comments…"
          />
        </div>

        <button
          onClick={() => setSubmitted(true)}
          disabled={!actualCash}
          className="btn-primary w-full"
        >
          <CheckCircle2 size={15} />
          Submit Reconciliation
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

function DriverDashboardContent() {
  const [deliveries, setDeliveries] = useState<Delivery[]>(initialDeliveries);
  const [cashModal, setCashModal] = useState<Delivery | null>(null);
  const [filter, setFilter] = useState<'all' | DeliveryStatus>('all');

  const handleStatusChange = (id: string, status: DeliveryStatus) => {
    setDeliveries(prev => prev.map(d => d.id === id ? { ...d, status } : d));
  };

  const handleCashSubmit = (id: string, _entry: CashEntry) => {
    setDeliveries(prev => prev.map(d => d.id === id ? { ...d, status: 'delivered' } : d));
    setCashModal(null);
  };

  const filtered = filter === 'all' ? deliveries : deliveries.filter(d => d.status === filter);

  const filterTabs: { key: 'all' | DeliveryStatus; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'pending', label: 'Pending' },
    { key: 'en-route', label: 'En Route' },
    { key: 'arrived', label: 'Arrived' },
    { key: 'delivered', label: 'Delivered' },
  ];

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background">
      <DriverTopbar />

      <main className="flex-1 overflow-y-auto scrollbar-thin">
        <div className="max-w-2xl mx-auto px-4 py-5 sm:px-6">

          {/* Page Header */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-1">
              <Circle size={8} className="fill-positive text-positive" />
              <span className="text-xs font-600 text-positive uppercase tracking-wide">Active Shift</span>
            </div>
            <h1 className="text-xl font-700 text-foreground">My Deliveries</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Wednesday, 30 July 2026 · St. John's Route</p>
          </div>

          {/* Summary Strip */}
          <SummaryStrip deliveries={deliveries} />

          {/* Filter Tabs */}
          <div className="flex gap-1.5 mb-4 overflow-x-auto scrollbar-thin pb-1">
            {filterTabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-600 transition-colors ${
                  filter === tab.key
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                }`}
              >
                {tab.label}
                {tab.key !== 'all' && (
                  <span className="ml-1.5 opacity-70">
                    {deliveries.filter(d => d.status === tab.key).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Delivery Cards */}
          <div className="space-y-3 mb-6">
            {filtered.length === 0 ? (
              <div className="card-elevated p-8 text-center">
                <Package size={32} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No deliveries in this category</p>
              </div>
            ) : (
              filtered.map(delivery => (
                <DeliveryCard
                  key={delivery.id}
                  delivery={delivery}
                  onStatusChange={handleStatusChange}
                  onOpenCash={setCashModal}
                />
              ))
            )}
          </div>

          {/* End of Day Reconciliation */}
          <ReconciliationPanel deliveries={deliveries} />

          {/* Bottom padding */}
          <div className="h-6" />
        </div>
      </main>

      {/* Cash Collection Modal */}
      {cashModal && (
        <CashCollectionModal
          delivery={cashModal}
          onClose={() => setCashModal(null)}
          onSubmit={handleCashSubmit}
        />
      )}
    </div>
  );
}

export default function DriverDashboardPage() {
  return (
    <ThemeProvider>
      <DriverDashboardContent />
    </ThemeProvider>
  );
}
